import { useCallback, useState } from 'react';

import { toast } from 'sonner';

import { DeletionHistory } from '../../../api/types/backend-dto-types';
import { DEFAULT_STATS } from '../../pages/matters/:matterId/settings/file-history/utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { useGetFileHistoryStats } from '@/api/queries/useGetFileHistoryStats';
import { SkippedFile, ProcessedDoc, NotProcessedDoc, File } from '@/api/types/backend-dto-types';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { downloadUploadOverviewReport } from '@/helpers/report-utils';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { SkippedFilesExplainerModal } from '@/new/pages/matters/:matterId/settings/file-history/components/modals/skipped-files-explainer-modal';
import { UploadFlowChart } from '@/new/pages/matters/:matterId/settings/file-history/components/upload-flow-chart';

interface UploadBreakdownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
}

export function UploadBreakdownModal({ open, onOpenChange, matterId }: UploadBreakdownModalProps) {
  const { data: stats, isLoading } = useGetFileHistoryStats(matterId);
  const resolvedStats = stats ?? DEFAULT_STATS;
  const [isDownloading, setIsDownloading] = useState(false);
  const [skippedFilesExplainerModalOpen, setSkippedFilesExplainerModalOpen] = useState(false);
  const { fetchConfigGET } = useGetFetchConfig();

  const handleDownloadReport = useCallback(async () => {
    if (!matterId || resolvedStats.totalAttempted === 0) return;

    setIsDownloading(true);
    try {
      trackCustomEvent(TrackingEventsV2.FileHistoryDownloadOverviewReport, { matterId });

      const [skippedRes, deletedRes, awaitingRes, processedRes, notProcessedRes] = await Promise.all([
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/skipped`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/deleted`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/files`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/processed`, fetchConfigGET),
        fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/not-processed`, fetchConfigGET),
      ]);

      const [skippedFiles, deletedFiles, awaitingFiles, processedDocs, notProcessedDocs] = await Promise.all([
        skippedRes.json() as Promise<SkippedFile[]>,
        deletedRes.json() as Promise<DeletionHistory[]>,
        awaitingRes.json() as Promise<File[]>,
        processedRes.json() as Promise<ProcessedDoc[]>,
        notProcessedRes.json() as Promise<NotProcessedDoc[]>,
      ]);

      downloadUploadOverviewReport(
        {
          stats: resolvedStats,
          skippedFiles,
          deletedFiles,
          awaitingFiles,
          processedDocs,
          notProcessedDocs,
        },
        matterId,
      );
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsDownloading(false);
    }
  }, [resolvedStats, matterId, fetchConfigGET]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--git-branch h-4.5 w-4.5 text-blue-500" />
            Upload Flow
          </DialogTitle>
          <DialogDescription>See how your files flow through the upload and processing pipeline</DialogDescription>
        </DialogHeader>

        <div>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Skeleton className="h-48 w-full" />
            </div>
          ) : stats ? (
            <UploadFlowChart matterId={matterId} stats={stats} linksEnabled={false} />
          ) : (
            <div className="flex h-64 flex-col items-center justify-center text-gray-400">
              <Icon className="lucide--git-branch h-10 w-10 opacity-40" />
              <p className="mt-2 text-sm">No upload data yet</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="default" size="sm" onClick={handleDownloadReport} disabled={isDownloading}>
            {isDownloading ? (
              <Icon className="lucide--loader-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Icon className="lucide--download h-3.5 w-3.5" />
            )}
            {isDownloading ? 'Downloading...' : 'Download Report'}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              setSkippedFilesExplainerModalOpen(true);
              trackCustomEvent(TrackingEventsV2.FileHistoryOpenSkippedExplainer, { matterId });
            }}
          >
            <Icon className="lucide--info h-3.5 w-3.5" />
            Why are files skipped?
          </Button>
        </div>
        <SkippedFilesExplainerModal
          open={skippedFilesExplainerModalOpen}
          onOpenChange={setSkippedFilesExplainerModalOpen}
        />
      </DialogContent>
    </Dialog>
  );
}
